/**
 * ✏️ MANAGE YACHT PRICING DIALOG
 * Dialog for viewing, updating, and deleting yacht destination pricing
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Box,
  Chip,
  Stack,
  InputAdornment,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import { Place, DirectionsBoat, Edit, Delete, Add, AccessTime } from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import {
  yachtDestinationPricingService,
  YachtWithPricing,
  YachtDestinationPricing,
  UpdateYachtPricingData,
} from '@/services/yachtDestinationPricingService';
import SetYachtPricingDialog from './SetYachtPricingDialog';
import { YachtDestination, yachtDestinationsService } from '@/services/yachtDestinationsService';

interface ManageYachtPricingDialogProps {
  open: boolean;
  yacht: YachtWithPricing;
  onClose: () => void;
  onSuccess: () => void;
}

export const ManageYachtPricingDialog: React.FC<ManageYachtPricingDialogProps> = ({
  open,
  yacht,
  onClose,
  onSuccess,
}) => {

  const [loading, setLoading] = useState(true);
  const [pricings, setPricings] = useState<YachtDestinationPricing[]>([]);
  const [destinations, setDestinations] = useState<YachtDestination[]>([]);
  const [editingPricing, setEditingPricing] = useState<string | null>(null);
  const [editData, setEditData] = useState<{
    oneWayPrice: number;
    roundTripPrice: number;
    hours: number;
    minutes: number;
    isAvailable: boolean;
    notes: string;
  }>({
    oneWayPrice: 0,
    roundTripPrice: 0,
    hours: 0,
    minutes: 0,
    isAvailable: true,
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);

  useEffect(() => {
    if (open) {
      loadPricing();
      loadDestinations();
    }
  }, [open, yacht.yachtId]);

  const loadPricing = async () => {
    setLoading(true);
    try {
      const response = await yachtDestinationPricingService.getYachtPricing(yacht.yachtId);
      if (response.success && response.pricings) {
        setPricings(response.pricings);
      } else {
        // Don't show error toast for backend not implemented yet
        if (response.error?.includes('404') || response.error?.includes('not found')) {
          setPricings([]);
        } else {
          toast.error(response.error || 'Failed to load pricing');
        }
      }
    } catch (error) {
      // Don't show error toast for network issues - backend may not be ready
      setPricings([]);
    } finally {
      setLoading(false);
    }
  };

  const loadDestinations = async () => {
    try {
      const response = await yachtDestinationsService.getDestinations({ active: true }).catch(err => {
        return { success: false, error: 'Backend endpoint not implemented yet' };
      });

      if (response.success && response.data?.destinations) {
        setDestinations(response.data.destinations);
      }
    } catch (error) {
    }
  };

  const handleEdit = (pricing: YachtDestinationPricing) => {
    setEditingPricing(pricing.id);
    const hours = Math.floor(pricing.estimatedDurationMinutes / 60);
    const minutes = pricing.estimatedDurationMinutes % 60;

    setEditData({
      oneWayPrice: pricing.oneWayPrice / 100, // Convert from kobo to naira
      roundTripPrice: pricing.roundTripPrice / 100,
      hours,
      minutes,
      isAvailable: pricing.isAvailable,
      notes: pricing.notes || '',
    });
  };

  const handleCancelEdit = () => {
    setEditingPricing(null);
    setEditData({
      oneWayPrice: 0,
      roundTripPrice: 0,
      hours: 0,
      minutes: 0,
      isAvailable: true,
      notes: '',
    });
  };

  const handleUpdate = async (pricingId: string) => {
    setSubmitting(true);
    try {
      const totalMinutes = (editData.hours * 60) + editData.minutes;

      const data: UpdateYachtPricingData = {
        oneWayPrice: Math.round(editData.oneWayPrice * 100),
        roundTripPrice: Math.round(editData.roundTripPrice * 100),
        estimatedDurationMinutes: totalMinutes,
        isAvailable: editData.isAvailable,
        notes: editData.notes.trim() || undefined,
      };

      const response = await yachtDestinationPricingService.updatePricing(pricingId, data);
      if (response.success) {
        toast.success('Pricing updated successfully!');
        setEditingPricing(null);
        await loadPricing();
      } else {
        toast.error(response.error || 'Failed to update pricing');
      }
    } catch (error) {
      console.error('Error updating pricing:', error);
      toast.error('Failed to update pricing');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (pricingId: string) => {
    if (!confirm('Are you sure you want to delete this pricing? This action cannot be undone.')) {
      return;
    }

    setSubmitting(true);
    try {
      const response = await yachtDestinationPricingService.deletePricing(pricingId);
      if (response.success) {
        toast.success('Pricing deleted successfully!');
        await loadPricing();
      } else {
        toast.error(response.error || 'Failed to delete pricing');
      }
    } catch (error) {
      console.error('Error deleting pricing:', error);
      toast.error('Failed to delete pricing');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amountInKobo: number) => {
    return `₦${(amountInKobo / 100).toLocaleString('en-NG', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
    if (hours > 0) return `${hours}h`;
    return `${mins}m`;
  };

  const handleAddSuccess = () => {
    setShowAddDialog(false);
    loadPricing();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6">Manage Yacht Destination Pricing</Typography>
              <Typography variant="caption" color="text.secondary">
                {yacht.yachtName} • {yacht.yachtType}
                {yacht.city && yacht.country ? ` • Homeport: ${yacht.city}, ${yacht.country}` :
                 yacht.homeport ? ` • Homeport: ${yacht.homeport}` :
                 yacht.city ? ` • Homeport: ${yacht.city}` :
                 ' • Location not set'}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setShowAddDialog(true)}
              size="small"
            >
              Add Destination
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : pricings.length === 0 ? (
            <Alert severity="info">
              No destination pricing configured for this yacht yet. Click "Add Destination" to set up pricing
              for different coastal destinations.
            </Alert>
          ) : (
            <Stack spacing={2}>
              {pricings.map((pricing) => {
                const isEditing = editingPricing === pricing.id;

                return (
                  <Card key={pricing.id} variant="outlined">
                    <CardContent>
                      {/* Header */}
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        sx={{ mb: 2 }}
                      >
                        <Box>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                            <Place fontSize="small" color="primary" />
                            <Typography variant="body2" fontWeight={600}>
                              {pricing.destination?.destinationName}
                            </Typography>
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {pricing.destination?.city}, {pricing.destination?.country}
                            {pricing.destination?.estimatedDistanceKm &&
                              ` • ${pricing.destination.estimatedDistanceKm} km`}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={0.5}>
                          {pricing.isAvailable ? (
                            <Chip label="Active" size="small" color="success" />
                          ) : (
                            <Chip label="Inactive" size="small" color="default" />
                          )}
                        </Stack>
                      </Stack>

                      <Divider sx={{ my: 2 }} />

                      {isEditing ? (
                        /* Edit Mode */
                        <Stack spacing={2}>
                          <TextField
                            label="One-Way Price"
                            type="number"
                            value={editData.oneWayPrice || ''}
                            onChange={(e) =>
                              setEditData({ ...editData, oneWayPrice: parseFloat(e.target.value) || 0 })
                            }
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                            }}
                            size="small"
                          />
                          <TextField
                            label="Round-Trip Price"
                            type="number"
                            value={editData.roundTripPrice || ''}
                            onChange={(e) =>
                              setEditData({ ...editData, roundTripPrice: parseFloat(e.target.value) || 0 })
                            }
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                            }}
                            size="small"
                          />
                          <Stack direction="row" spacing={2}>
                            <TextField
                              label="Hours"
                              type="number"
                              value={editData.hours || ''}
                              onChange={(e) =>
                                setEditData({ ...editData, hours: parseInt(e.target.value) || 0 })
                              }
                              InputProps={{
                                startAdornment: <InputAdornment position="start"><AccessTime /></InputAdornment>,
                              }}
                              size="small"
                              sx={{ flex: 1 }}
                            />
                            <TextField
                              label="Minutes"
                              type="number"
                              value={editData.minutes || ''}
                              onChange={(e) =>
                                setEditData({ ...editData, minutes: parseInt(e.target.value) || 0 })
                              }
                              size="small"
                              sx={{ flex: 1 }}
                            />
                          </Stack>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={editData.isAvailable}
                                onChange={(e) =>
                                  setEditData({ ...editData, isAvailable: e.target.checked })
                                }
                              />
                            }
                            label="Available for booking"
                          />
                          <TextField
                            label="Notes"
                            multiline
                            rows={2}
                            value={editData.notes}
                            onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                            size="small"
                          />
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button onClick={handleCancelEdit} disabled={submitting} size="small">
                              Cancel
                            </Button>
                            <Button
                              onClick={() => handleUpdate(pricing.id)}
                              variant="contained"
                              disabled={submitting}
                              size="small"
                            >
                              {submitting ? 'Saving...' : 'Save Changes'}
                            </Button>
                          </Stack>
                        </Stack>
                      ) : (
                        /* View Mode */
                        <>
                          <Stack spacing={1.5}>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                One-Way Price
                              </Typography>
                              <Typography variant="body1" fontWeight={600}>
                                {formatCurrency(pricing.oneWayPrice)}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Round-Trip Price
                              </Typography>
                              <Typography variant="body1" fontWeight={600}>
                                {formatCurrency(pricing.roundTripPrice)}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Estimated Duration
                              </Typography>
                              <Typography variant="body1" fontWeight={600}>
                                {formatDuration(pricing.estimatedDurationMinutes)}
                              </Typography>
                            </Box>
                            {pricing.notes && (
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Notes
                                </Typography>
                                <Typography variant="body2">{pricing.notes}</Typography>
                              </Box>
                            )}
                          </Stack>

                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="flex-end"
                            sx={{ mt: 2 }}
                          >
                            <Tooltip title="Edit Pricing">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(pricing)}
                                disabled={submitting}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Pricing">
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(pricing.id)}
                                disabled={submitting}
                                color="error"
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Destination Pricing Dialog */}
      {showAddDialog && (
        <SetYachtPricingDialog
          open={showAddDialog}
          yacht={yacht}
          destinations={destinations}
          onClose={() => setShowAddDialog(false)}
          onSuccess={handleAddSuccess}
        />
      )}
    </>
  );
};

export default ManageYachtPricingDialog;
